import { useState } from "react";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiChip,
  MiDetailFields,
  MiIcon,
  MiIconButton,
  MiInputTextbox,
  MiProfilePicture,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  INPUT_TYPES,
  LANGUAGES,
  PERMISSIONS,
  TIMEZONE_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { Sha256Hash, getPhoneNumber } from "@miplatform/utils";
import {
  mdiAccount,
  mdiAccountCog,
  mdiCog,
  mdiEye,
  mdiEyeOffOutline,
  mdiPhone,
  mdiTools,
} from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import {
  Box,
  Divider,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { usePermissions } from "../../hooks";
import { schema } from "../../views/AdminSettings/ManageUser/userSchema";

export const USER_PROFILE_FORM_SECTIONS = {
  BASIC_INFO: "Basic Info",
  CONTACT_INFO: "Contact Information",
  PREFERENCES: "Preferences",
  ADMIN: "Admin",
  USER_SETTINGS: "User Settings",
};

const statusColors = {
  Active: "success",
  Pending: "secondary",
};

const PinInput = ({
  pinCond,
  handleChange,
  onChange,
  pinVisible,
  updating,
  setPinVisible,
  setUpdating,
  error,
}) => {
  const [pin, setPin] = useState("");
  const onChangeHandler = (e) => {
    setPin(e.target.value);
    (handleChange ?? onChange)?.(e);
  };
  if (updating) {
    return (
      <MiInputTextbox
        type={pinVisible ? "text" : "password"}
        value={pin}
        onChange={onChangeHandler}
        error={error}
        name="pin"
        label="Pin"
        variant="outlined"
        margin="normal"
        textfieldProps={{
          endAdornment: (
            <InputAdornment position="end">
              <MiIconButton onClick={() => setPinVisible(!pinVisible)}>
                <MiIcon
                  path={pinVisible ? mdiEyeOffOutline : mdiEye}
                  size={1}
                />
              </MiIconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  }
  return (
    <MiButton
      title={pinCond ? "Reset Pin" : "Create Pin"}
      variant={pinCond ? BUTTON_VARIANTS.OUTLINED : BUTTON_VARIANTS.CONTAINED}
      color={pinCond ? "error" : "primary"}
      onClick={() => {
        setPin("");
        setUpdating(true);
      }}
    />
  );
};

export const UserProfile = ({
  user,
  roles = [],
  handleUpdate = async () => {},
  isLoading = false,
  sections = [],
  disableReadOnly,
  uploadProfilePhoto = () => {},
  deleteProfilePhoto = () => {},
  getProfilePhotoUrl = () => null,
}) => {
  const hasPin = user?.pinHash !== undefined && user?.pinHash !== null;

  const [isPinVisible, setIsPinVisible] = useState(false);

  const [updatingPin, setUpdatingPin] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { hasPermission } = usePermissions();
  const canUpdateUserProfile =
    hasPermission(PERMISSIONS.USER.UPDATE) || disableReadOnly;

  const { classes } = useStyles();

  const sectionsConfigMap = {
    [USER_PROFILE_FORM_SECTIONS.BASIC_INFO]: {
      icon: mdiAccount,
      title: USER_PROFILE_FORM_SECTIONS.BASIC_INFO,
      variant: BUTTON_VARIANTS.CONTAINED,
      fields: [
        {
          label: "First Name",
          name: "firstName",
          required: true,
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.firstName,
          readOnly: !canUpdateUserProfile,
        },
        {
          label: "Last Name",
          name: "lastName",
          required: true,
          width: "50%",
          minWidth: "180px",
          readOnly: !canUpdateUserProfile,
        },
        {
          label: "User Name",
          name: "userName",
          disabled: true,
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.userName,
          readOnly: !canUpdateUserProfile,
        },
        {
          label: "Display Name",
          required: true,
          name: "displayName",
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.displayName,
          readOnly: !canUpdateUserProfile,
        },
      ],
    },
    [USER_PROFILE_FORM_SECTIONS.CONTACT_INFO]: {
      icon: mdiPhone,
      title: USER_PROFILE_FORM_SECTIONS.CONTACT_INFO,
      variant: BUTTON_VARIANTS.CONTAINED,
      fields: [
        {
          label: "Email",
          name: "email",
          required: true,
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.email,
          readOnly: !canUpdateUserProfile,
        },
        {
          label: "Phone",
          name: "primaryPhoneNumber",
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.primaryPhoneNumber,
          readOnly: !canUpdateUserProfile,
          handleChange: (e) => {
            const phoneNumber = getPhoneNumber(e.target.value);
            if (validatePhoneNumberLength(phoneNumber, "US") === "TOO_LONG") {
              return;
            }
            edit.update({ primaryPhoneNumber: phoneNumber });
          },
        },
      ],
    },
    [USER_PROFILE_FORM_SECTIONS.PREFERENCES]: {
      icon: mdiTools,
      title: USER_PROFILE_FORM_SECTIONS.PREFERENCES,
      variant: BUTTON_VARIANTS.CONTAINED,
      fields: [
        {
          label: "Language",
          name: "language",
          fieldType: INPUT_TYPES.SELECT,
          options: LANGUAGES,
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.language,
          readOnly: !canUpdateUserProfile,
        },
        {
          label: "Timezone",
          name: "timezone",
          fieldType: INPUT_TYPES.SELECT,
          options: TIMEZONE_OPTIONS,
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.timezone,
          readOnly: !canUpdateUserProfile,
        },
      ],
    },
    [USER_PROFILE_FORM_SECTIONS.ADMIN]: {
      icon: mdiAccountCog,
      title: USER_PROFILE_FORM_SECTIONS.ADMIN,
      variant: BUTTON_VARIANTS.CONTAINED,
      fields: [
        {
          label: "Title",
          name: "title",
          minWidth: "180px",
          width: "50%",
          validationRule: schema.shape.title,
          readOnly: !canUpdateUserProfile,
        },
        {
          label: "Roles",
          name: "roleIds",
          options: roles,
          width: "50%",
          minWidth: "180px",
          fieldType: INPUT_TYPES.MULTI_SELECT,
          required: true,
          validationRule: schema.shape.roleIds,
          disabled: !canUpdateUserProfile,
        },
        {
          field: "status",
          headerName: "Status",
          headerAlign: "left",
          sortable: false,
          align: "left",
          fieldType: INPUT_TYPES.CUSTOM,
          groupable: false,
          customComponent: (
            <MiChip
              color={statusColors[user?.status] || "primary"}
              clickable={false}
              label={user?.status}
            />
          ),
          readOnly: !canUpdateUserProfile,
        },
        {
          label: "Disabled",
          name: "isDisabled",
          minWidth: "180px",
          fieldType: INPUT_TYPES.CHECKBOX,
          readOnly: !canUpdateUserProfile,
        },
      ],
    },
    [USER_PROFILE_FORM_SECTIONS.USER_SETTINGS]: {
      icon: mdiCog,
      title: USER_PROFILE_FORM_SECTIONS.USER_SETTINGS,
      variant: BUTTON_VARIANTS.CONTAINED,
      fields: [
        {
          label: "PIN",
          name: "pin",
          width: "100%",
          minWidth: "180px",
          required: false,
          validationRule: schema.shape.pin,
          fieldType: INPUT_TYPES.CUSTOM,
          // customComponent is created below.
        },
      ],
    },
  };

  const edit = useEdit(user, [
    ...sections.reduce(
      (acc, section) => [...acc, ...(sectionsConfigMap[section]?.fields ?? [])],
      []
    ),
  ]);

  const profilePhotoUrl = getProfilePhotoUrl(user?.photo?.id);

  const getSectionFields = (section) => {
    const fields = section.fields.map((f) => {
      if (f.name !== "pin") return f;

      const pinField = {
        ...f,
        value: "",
        handleChange: (e) => {
          edit.update({ pin: e.target.value });
        },
        customComponent: (
          <PinInput
            pinCond={hasPin}
            pinVisible={isPinVisible}
            updating={updatingPin}
            setPinVisible={setIsPinVisible}
            setUpdating={setUpdatingPin}
            error={edit.errors?.pin}
          />
        ),
      };
      return pinField;
    });

    return (
      <MiDetailFields
        containerStyle={classes.containerStyle}
        customWrapperStyle={classes.detailFields}
        detailFields={fields}
        headerProps={{
          icon: section.icon,
          variant: section.variant,
          title: section.title,
        }}
        edit={edit}
      />
    );
  };

  return (
    <>
      <MiBox className={classes.container} overflow="auto">
        <Box className={classes.pictureContainer}>
          <MiProfilePicture
            src={profilePhotoUrl}
            name={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
            onEdit={uploadProfilePhoto}
            onDelete={profilePhotoUrl ? deleteProfilePhoto : undefined}
            disabled={isLoading || !canUpdateUserProfile}
            size={130}
          />
          <Box>
            <Typography variant="h5" align="center">{`${
              user?.firstName ?? ""
            } ${user?.lastName ?? ""}`}</Typography>
            <Typography variant="body2" align="center">
              {user?.displayName ?? ""}
            </Typography>
          </Box>
        </Box>
        <Divider orientation={isSmallScreen ? "horizontal" : "vertical"} />
        <Box className={classes.detailsFlex}>
          {sections.map((section) =>
            getSectionFields(sectionsConfigMap[section])
          )}
        </Box>
      </MiBox>
      <Footer
        show={edit.isDirty()}
        handleSaveClick={async () => {
          const updates = edit.handleSubmit();
          if (updates?.pin) {
            updates.pin = await Sha256Hash(updates.pin);
          }
          const response = await handleUpdate(updates, edit);
          if (!response || response.status < 400 || response.canceled) {
            edit.reset();
            setUpdatingPin(false);
          }
        }}
        handleCancelClick={() => {
          edit.reset();
          setUpdatingPin(false);
        }}
        disableSave={!edit.isValid() || isLoading}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    overflowY: "auto",
    height: "inherit",
    display: "flex",
    gap: theme.spacing(3),
    flexDirection: "row",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
    backgroundColor: theme.palette.common.white,
  },
  detailsFlex: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing(8),
  },
  pictureContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: theme.spacing(5),
    padding: theme.spacing(0, 3),
    flex: "1 1 25%",
    [theme.breakpoints.down("md")]: {
      flex: "0 1 25%",
    },
    gap: theme.spacing(3),
  },
  containerStyle: {
    display: "flex",
    flex: "1 1 45%",
    flexDirection: "column",
  },
  detailFields: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    padding: theme.spacing(0, 4),
  },
}));
